// src/utils/sottoattivitaElenco.js
// ═══════════════════════════════════════════════════════════════════════════
// SOTTOATTIVITÀ VISIBILI NEGLI ELENCHI (ottobre 2026, stile Asana)
// ═══════════════════════════════════════════════════════════════════════════
// Nell'elenco della commessa e nel Kanban, accanto al nome di un'attività che ha
// sottoattività compare «⑂ 2/5» (completate / totali): si capisce subito che dentro c'è
// altro lavoro. Nell'elenco la freccia apre le sottoattività sotto la riga.
//  • useSottoattivitaDi: carica (in tempo reale) le sottoattività di un gruppo di attività,
//    raggruppate per attività madre. Stessa query già usata altrove (organizationId +
//    parentTaskId «in» a blocchi di 30): nessuna regola né indice nuovo.
//  • spuntaSottoattivita: la spunta con le stesse regole del pannello dell'attività
//    (chi può completare, «Completata da», avvisi, messaggio viola).
// ═══════════════════════════════════════════════════════════════════════════
import { useEffect, useState } from "react";
import { collection, doc, onSnapshot, query, serverTimestamp, updateDoc, where } from "firebase/firestore";
import { db } from "../firebase";
import { puoGestireSottoattivita } from "./condivisione";
import { campiCompletamento, avvisoCompletamentoDiAltri } from "./completamentoAttivita";
import { avvisaCambioStatoSottoattivita } from "./avvisiAttivita";

const ordina = list => list.sort((a, b) => (a.createdAt?.toMillis?.() || 0) - (b.createdAt?.toMillis?.() || 0));

/** { [taskId]: [sottoattività in ordine di creazione] } per le attività indicate. */
export function useSottoattivitaDi(taskIds, organizationId) {
  const chiave = [...new Set((taskIds || []).filter(Boolean))].sort().join(",");
  const [mappa, setMappa] = useState({});
  useEffect(() => {
    if (!chiave || !organizationId) { setMappa({}); return undefined; }
    const ids = chiave.split(",");
    const blocchi = [];
    for (let i = 0; i < ids.length; i += 30) blocchi.push(ids.slice(i, i + 30));
    const perBlocco = blocchi.map(() => []);
    const aggiorna = () => {
      const m = {};
      perBlocco.flat().forEach(s => { (m[s.parentTaskId] = m[s.parentTaskId] || []).push(s); });
      Object.values(m).forEach(ordina);
      setMappa(m);
    };
    const stop = blocchi.map((b, i) => onSnapshot(
      query(collection(db, "subtasks"), where("organizationId", "==", organizationId), where("parentTaskId", "in", b)),
      snap => { perBlocco[i] = snap.docs.map(d => ({ id: d.id, ...d.data() })); aggiorna(); },
      err => console.warn("Sottoattività non caricate:", err?.code || err)));
    return () => stop.forEach(f => f());
  }, [chiave, organizationId]);
  return mappa;
}

/** Completate / totali (le annullate non contano). */
export function contaSottoattivita(lista) {
  const valide = (lista || []).filter(s => s.status !== "annullata");
  return { fatte: valide.filter(s => s.status === "completata").length, totali: valide.length };
}

/**
 * Spunta / riapre una sottoattività con le regole di sempre. ruoloAttivita: "pieno" |
 * "collabora" (utils/condivisione.js). addToast facoltativo per i messaggi.
 */
export async function spuntaSottoattivita(st, { ruoloAttivita = "pieno", utente, projectId = null, addToast = null }) {
  if (!puoGestireSottoattivita(st, ruoloAttivita, utente?.uid)) {
    if (addToast) addToast(`Questa sottoattività la può completare chi l'ha creata${st.assignedName ? `, ${st.assignedName}` : " o chi ne è incaricato"} o chi gestisce l'attività`, "viola", 7000);
    return false;
  }
  const dopo = st.status === "completata" ? "aperta" : "completata";
  await updateDoc(doc(db, "subtasks", st.id), { status: dopo, ...campiCompletamento(dopo, utente), updatedAt: serverTimestamp() });
  avvisaCambioStatoSottoattivita(st, dopo, utente, { projectId });
  const viola = dopo === "completata" ? avvisoCompletamentoDiAltri(st, utente?.uid, true) : null;
  if (viola && addToast) addToast(viola, "viola", 8000);
  return true;
}
